import { useState, Fragment } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type Entry = {
  id: number
  work_date: string
  hours: string
  task_name: string | null
  description: string
}

type Report = {
  id: number
  project_code: string
  project_name: string
  employee_name: string
  period_start: string
  period_end: string
  status: 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED'
  total_hours: string
  notes: string
  submitted_at: string | null
  approved_by_username: string | null
  approved_at: string | null
  rejection_reason: string
  generated_bill: number | null
  generated_bill_number: string | null
  effective_rate: string
  effective_rate_unit: 'HOUR' | 'DAY'
  effective_rate_per_hour: string
  effective_currency: string
  entries: Entry[]
}

const STATUS_BADGE: Record<Report['status'], string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  SUBMITTED: 'bg-blue-100 text-blue-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-700',
}

export default function ConsultingApprovals() {
  const qc = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'ALL'>('SUBMITTED')
  const [expanded, setExpanded] = useState<number | null>(null)
  const [rejectingId, setRejectingId] = useState<number | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const { data: reports = [], isLoading } = useQuery({
    queryKey: ['consulting', 'work-reports', 'approvals', statusFilter],
    queryFn: async () => {
      const url = statusFilter === 'ALL'
        ? '/consulting/work-reports/'
        : `/consulting/work-reports/?status=${statusFilter}`
      return (await api.get<Report[]>(url)).data
    },
  })

  const approveMut = useMutation({
    mutationFn: async (id: number) => (await api.post(`/consulting/work-reports/${id}/approve/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'work-reports', 'approvals'] }),
  })

  const rejectMut = useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) =>
      (await api.post(`/consulting/work-reports/${id}/reject/`, { reason })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['consulting', 'work-reports', 'approvals'] })
      setRejectingId(null)
      setRejectReason('')
    },
  })

  const generateBillMut = useMutation({
    mutationFn: async (id: number) =>
      (await api.post(`/consulting/work-reports/${id}/generate-bill/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consulting', 'work-reports', 'approvals'] }),
  })

  const error =
    (approveMut.error as any)?.response?.data?.detail ||
    (rejectMut.error as any)?.response?.data?.detail ||
    (generateBillMut.error as any)?.response?.data?.detail

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Work Report Approvals</h2>
          <p className="text-sm text-gray-500">
            Submitted hours from your project teams. Approve to lock; generate a draft Bill for the vendor when ready.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm text-gray-600">Show:</span>
        {(['SUBMITTED', 'APPROVED', 'REJECTED', 'ALL'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-xs px-3 py-1 rounded font-semibold ${
              statusFilter === s ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{error}</div>
      )}

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : reports.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-500">
          Nothing in this queue.
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-2 font-semibold text-gray-600 w-6" />
                <th className="px-4 py-2 font-semibold text-gray-600">Consultant</th>
                <th className="px-4 py-2 font-semibold text-gray-600">Project</th>
                <th className="px-4 py-2 font-semibold text-gray-600">Period</th>
                <th className="px-4 py-2 font-semibold text-gray-600">Hours / amount</th>
                <th className="px-4 py-2 font-semibold text-gray-600">Status</th>
                <th className="px-4 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => {
                const open = expanded === r.id
                // Always multiply hours by per-hour rate. effective_rate_per_hour
                // is normalized by the backend even when the underlying vendor
                // contract is priced per day.
                const amount = (parseFloat(r.total_hours) * parseFloat(r.effective_rate_per_hour)).toFixed(2)
                return (
                  <Fragment key={r.id}>
                    <tr className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-2 py-2">
                        <button
                          onClick={() => setExpanded(open ? null : r.id)}
                          className="text-gray-500 hover:text-gray-900"
                        >
                          {open ? '▼' : '▶'}
                        </button>
                      </td>
                      <td className="px-4 py-2">{r.employee_name}</td>
                      <td className="px-4 py-2">
                        <div className="font-mono text-xs text-gray-500">{r.project_code}</div>
                        <div>{r.project_name}</div>
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-600">{r.period_start} → {r.period_end}</td>
                      <td className="px-4 py-2">
                        <div className="font-semibold">{r.total_hours}h</div>
                        <div className="text-xs text-gray-500">≈ {amount} {r.effective_currency}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_BADGE[r.status]}`}>
                          {r.status}
                        </span>
                        {r.generated_bill_number && (
                          <div className="text-xs text-gray-500 mt-1">→ {r.generated_bill_number}</div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right text-xs">
                        {r.status === 'SUBMITTED' && (
                          <>
                            <button
                              onClick={() => approveMut.mutate(r.id)}
                              disabled={approveMut.isPending}
                              className="text-green-700 hover:underline mr-3 font-semibold"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => { setRejectingId(r.id); setRejectReason('') }}
                              className="text-red-600 hover:underline"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {r.status === 'APPROVED' && !r.generated_bill_number && (
                          <button
                            onClick={() => generateBillMut.mutate(r.id)}
                            disabled={generateBillMut.isPending}
                            className="text-blue-600 hover:underline font-semibold"
                          >
                            Generate Bill
                          </button>
                        )}
                      </td>
                    </tr>
                    {open && (
                      <tr className="bg-gray-50 border-t border-gray-100">
                        <td colSpan={7} className="px-6 py-3">
                          {r.notes && (
                            <div className="mb-3 text-xs text-gray-700">
                              <strong>Notes:</strong> {r.notes}
                            </div>
                          )}
                          {r.rejection_reason && (
                            <div className="mb-3 text-xs text-red-700">
                              <strong>Rejection:</strong> {r.rejection_reason}
                            </div>
                          )}
                          <table className="w-full text-xs">
                            <thead className="text-gray-500">
                              <tr>
                                <th className="text-left pb-1 pr-3">Date</th>
                                <th className="text-left pb-1 pr-3">Hours</th>
                                <th className="text-left pb-1">Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {r.entries.map((e) => (
                                <tr key={e.id}>
                                  <td className="py-1 pr-3">{e.work_date}</td>
                                  <td className="py-1 pr-3 font-mono">{e.hours}</td>
                                  <td className="py-1">{e.description || '—'}</td>
                                </tr>
                              ))}
                              {r.entries.length === 0 && (
                                <tr><td colSpan={3} className="py-1 text-gray-400">(no entries)</td></tr>
                              )}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {rejectingId !== null && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-gray-200">
              <h3 className="text-lg font-bold">Reject report</h3>
            </div>
            <div className="p-5">
              <Field>
                <FieldLabel htmlFor="reject-reason">Reason (shown to the consultant)</FieldLabel>
                <Textarea
                  id="reject-reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  placeholder="e.g. Hours on May 13 don't match what we agreed; please update."
                />
              </Field>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setRejectingId(null); setRejectReason('') }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => rejectMut.mutate({ id: rejectingId, reason: rejectReason })}
                disabled={rejectMut.isPending || !rejectReason.trim()}
              >
                {rejectMut.isPending ? 'Rejecting…' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
